import { useState, useEffect } from "react";
import { businessHoursApi } from "../lib/api";
import api from "../lib/api";
import { CURRENCY_OPTIONS, cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { 
  Clock, Save, Timer, Users, Clock3, Coins, Plus, Pencil, Trash2, 
  AlertCircle, Settings as SettingsIcon, Shield, UserPlus, Mail, Lock, Loader2,
  Check, X, Calendar, PartyPopper, CalendarX, BarChart3, DollarSign, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";

const SNAP_INTERVALS = [
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
];

const GAP_OPTIONS = [
  { value: 0, label: "No gap required" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

// Helper to get empty permissions object
const getEmptyPermissions = () => ({
  can_access_admin_dashboard: true,
  rota_view: false, rota_create: false, rota_edit: false, rota_delete: false,
  employees_view: false, employees_create: false, employees_edit: false, employees_delete: false,
  shifts_view: false, shifts_create: false, shifts_edit: false, shifts_delete: false,
  events_view: false, events_create: false, events_edit: false, events_delete: false,
  absences_view: false, absences_create: false, absences_edit: false, absences_delete: false,
  reports_view: false, reports_export: false,
  wages_view: false, wages_edit: false,
  settings_view: false, settings_edit: false,
  billing_view: false, billing_manage: false,
  roles_manage: false, users_manage: false
});

const Settings = () => {
  const { subscriptionTier, isCompanyAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("general");
  
  // General settings state
  const [snapInterval, setSnapInterval] = useState(15);
  const [allowMultipleShifts, setAllowMultipleShifts] = useState(true);
  const [shiftGapMinutes, setShiftGapMinutes] = useState(0);
  const [currency, setCurrency] = useState("GBP");
  const [shiftPatternsEnabled, setShiftPatternsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Admin Users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [adminUserDialogOpen, setAdminUserDialogOpen] = useState(false);
  const [editingAdminUser, setEditingAdminUser] = useState(null);
  const [adminUserForm, setAdminUserForm] = useState({
    name: "",
    email: "",
    user_role: "manager"
  });
  const [savingAdminUser, setSavingAdminUser] = useState(false);
  const [deleteAdminUserDialog, setDeleteAdminUserDialog] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  // Roles state
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: "",
    description: "",
    permissions: getEmptyPermissions()
  });
  const [savingRole, setSavingRole] = useState(false);
  const [deleteRoleDialog, setDeleteRoleDialog] = useState(null);

  // Fetch general settings
  const fetchData = async () => {
    try {
      setLoading(true);
      const hoursRes = await businessHoursApi.get();
      setSnapInterval(hoursRes.data.snap_interval || 15);
      setAllowMultipleShifts(hoursRes.data.allow_multiple_shifts !== false);
      setShiftGapMinutes(hoursRes.data.shift_gap_minutes || 0);
      setCurrency(hoursRes.data.currency || "GBP");
      setShiftPatternsEnabled(hoursRes.data.shift_patterns_enabled || false);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin users
  const fetchAdminUsers = async () => {
    setLoadingAdminUsers(true);
    try {
      const response = await api.get('/admin-users');
      setAdminUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      // If endpoint doesn't exist yet, just set empty array
      setAdminUsers([]);
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await api.get('/roles');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'admin-users' && isCompanyAdmin) {
      fetchAdminUsers();
      if (roles.length === 0) fetchRoles();
    } else if (activeTab === 'roles' && isCompanyAdmin) {
      fetchRoles();
    }
  }, [activeTab, isCompanyAdmin]);

  // Save general settings
  const handleSave = async () => {
    try {
      setSaving(true);
      const currentData = await businessHoursApi.get();
      await businessHoursApi.update({ 
        hours: currentData.data.hours, 
        snap_interval: snapInterval,
        allow_multiple_shifts: allowMultipleShifts,
        shift_gap_minutes: shiftGapMinutes,
        currency: currency,
        shift_patterns_enabled: shiftPatternsEnabled
      });
      window.dispatchEvent(new Event('settings-updated'));
      toast.success("Settings saved");
    } catch (error) {
      console.error("Error saving business hours:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Admin User management
  const openAdminUserDialog = (user = null) => {
    if (user) {
      setEditingAdminUser(user);
      setAdminUserForm({
        name: user.name,
        email: user.email,
        user_role: user.user_role || "manager"
      });
    } else {
      setEditingAdminUser(null);
      setAdminUserForm({
        name: "",
        email: "",
        user_role: "manager"
      });
    }
    setAdminUserDialogOpen(true);
  };

  const handleSaveAdminUser = async () => {
    if (!adminUserForm.name.trim() || !adminUserForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }

    setSavingAdminUser(true);
    try {
      if (editingAdminUser) {
        // Update existing user (can't change email for invited users who haven't set up)
        await api.put(`/admin-users/${editingAdminUser.id}`, adminUserForm);
        toast.success("Admin user updated");
      } else {
        // Invite new user
        const response = await api.post('/admin-users/invite', adminUserForm);
        if (response.data.email_sent) {
          toast.success(response.data.message || "Invitation sent!");
        } else {
          toast.warning("Admin user created but email failed to send. You can resend the invite.");
        }
      }
      setAdminUserDialogOpen(false);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save admin user");
    } finally {
      setSavingAdminUser(false);
    }
  };

  const handleResendInvite = async (userId) => {
    setResendingInvite(userId);
    try {
      const response = await api.post(`/admin-users/${userId}/resend-invite`);
      toast.success(response.data.message || "Invitation resent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invite");
    } finally {
      setResendingInvite(null);
    }
  };

  const handleDeleteAdminUser = async () => {
    if (!deleteAdminUserDialog) return;
    try {
      await api.delete(`/admin-users/${deleteAdminUserDialog.id}`);
      toast.success("Admin user deleted");
      setDeleteAdminUserDialog(null);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin user");
    }
  };

  // Role management
  const openCreateRoleDialog = () => {
    setEditingRole(null);
    setRoleFormData({
      name: "",
      description: "",
      permissions: getEmptyPermissions()
    });
    setCreateRoleDialogOpen(true);
  };

  const openEditRoleDialog = (role) => {
    setEditingRole(role);
    const perms = getEmptyPermissions();
    if (role.permissions) {
      Object.keys(perms).forEach(key => {
        perms[key] = role.permissions[key] || false;
      });
    }
    setRoleFormData({
      name: role.name,
      description: role.description || "",
      permissions: perms
    });
    setCreateRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleFormData.name.trim()) {
      toast.error('Please enter a role name');
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, roleFormData);
        toast.success('Role updated successfully');
      } else {
        await api.post('/roles', roleFormData);
        toast.success('Role created successfully');
      }
      setCreateRoleDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteRoleDialog) return;
    try {
      await api.delete(`/roles/${deleteRoleDialog.id}`);
      toast.success('Role deleted successfully');
      setDeleteRoleDialog(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permKey) => {
    setRoleFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: !prev.permissions[permKey]
      }
    }));
  };

  // Get role name from ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role) return role.name;
    const defaultNames = {
      'super_admin': 'Admin',
      'manager': 'Manager',
      'accounts': 'Accounts',
      'staff': 'Staff'
    };
    return defaultNames[roleId] || roleId;
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-muted-foreground">Configure your business preferences and team access</p>
        </div>
        {activeTab === 'general' && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        )}
        {activeTab === 'admin-users' && isCompanyAdmin && (
          <Button onClick={() => openAdminUserDialog()} data-testid="invite-admin-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Admin User
          </Button>
        )}
        {activeTab === 'roles' && isCompanyAdmin && (
          <Button onClick={openCreateRoleDialog} data-testid="create-role-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Role
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="general" data-testid="tab-general">
            <SettingsIcon className="w-4 h-4 mr-2 hidden sm:inline" />
            General
          </TabsTrigger>
          {isCompanyAdmin && (
            <TabsTrigger value="admin-users" data-testid="tab-admin-users">
              <Users className="w-4 h-4 mr-2 hidden sm:inline" />
              Admin Users
            </TabsTrigger>
          )}
          {isCompanyAdmin && (
            <TabsTrigger value="roles" data-testid="tab-roles">
              <Shield className="w-4 h-4 mr-2 hidden sm:inline" />
              Roles
            </TabsTrigger>
          )}
          <TabsTrigger value="shifts" data-testid="tab-shifts">
            <Clock className="w-4 h-4 mr-2 hidden sm:inline" />
            Shifts
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          {/* Currency Setting */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Currency
              </CardTitle>
              <CardDescription>
                Set the default currency for cost calculations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 p-4 rounded-lg border">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="currency" className="text-base font-medium">Default Currency</Label>
                  <p className="text-sm text-muted-foreground">
                    Used for displaying shift costs and employee hourly rates
                  </p>
                </div>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-48" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono w-6">{c.symbol}</span>
                          <span>{c.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Users Tab */}
        {isCompanyAdmin && (
          <TabsContent value="admin-users" className="space-y-6">
            {/* Info Card */}
            <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100">About Admin Users</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Admin users have access to the manager dashboard but are not scheduled on the rota. 
                      Perfect for accountants, HR staff, or office managers who need system access.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin Users List */}
            {loadingAdminUsers ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : adminUsers.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No admin users yet</h3>
                  <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
                    Invite admin users who need dashboard access but won't be scheduled on the rota
                  </p>
                  <Button onClick={() => openAdminUserDialog()}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Admin User
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {adminUsers.map((user) => (
                  <Card key={user.id} data-testid={`admin-user-${user.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary">
                              {user.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.name}</p>
                              {user.status === 'invited' ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                                  Pending
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                  <Check className="w-3 h-3 inline mr-0.5" />
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300">
                            {getRoleName(user.user_role)}
                          </span>
                          <div className="flex gap-1">
                            {user.status === 'invited' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => handleResendInvite(user.id)}
                                disabled={resendingInvite === user.id}
                                data-testid={`resend-invite-${user.id}`}
                              >
                                {resendingInvite === user.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Mail className="w-3 h-3 mr-1" />
                                    Resend
                                  </>
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openAdminUserDialog(user)}
                              data-testid={`edit-admin-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setDeleteAdminUserDialog(user)}
                              data-testid={`delete-admin-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Roles Tab */}
        {isCompanyAdmin && (
          <TabsContent value="roles" className="space-y-6">
            {/* Info Card */}
            <Card className="bg-violet-50/50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-violet-900 dark:text-violet-100">About Roles & Permissions</h3>
                    <p className="text-sm text-violet-700 dark:text-violet-300 mt-1">
                      Define roles with specific permissions for what users can view, create, edit, or delete.
                      Assign roles to both admin users and employees who need elevated access.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Roles List */}
            {loadingRoles ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Default Roles */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Default Roles</h3>
                  <div className="grid gap-3">
                    {roles.filter(r => r.is_default).map((role) => (
                      <Card key={role.id} data-testid={`role-card-${role.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold">{role.name}</h4>
                                </div>
                                <p className="text-sm text-muted-foreground mt-0.5">{role.description}</p>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Lock className="w-3.5 h-3.5" />
                              System
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Custom Roles */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Custom Roles</h3>
                  {roles.filter(r => !r.is_default).length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="flex flex-col items-center justify-center py-8">
                        <Sparkles className="w-10 h-10 text-muted-foreground mb-3" />
                        <h4 className="font-medium mb-1">No custom roles yet</h4>
                        <p className="text-sm text-muted-foreground mb-4 text-center">
                          Create custom roles with specific permissions for your team
                        </p>
                        <Button onClick={openCreateRoleDialog} variant="outline" size="sm">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Custom Role
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-3">
                      {roles.filter(r => !r.is_default).map((role) => (
                        <Card key={role.id} data-testid={`role-card-${role.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                  <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">{role.name}</h4>
                                    <span className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 px-1.5 py-0.5 rounded">Custom</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-0.5">{role.description || 'Custom role'}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openEditRoleDialog(role)}
                                  data-testid={`edit-role-${role.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setDeleteRoleDialog(role)}
                                  data-testid={`delete-role-${role.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        )}

        {/* Shifts Tab */}
        <TabsContent value="shifts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Shift Settings
              </CardTitle>
              <CardDescription>
                Configure how shifts are created and managed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Multiple Shifts Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <Label htmlFor="multiple-shifts" className="text-base font-medium">
                    Allow Multiple Shifts Per Employee
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Enable this to allow split shifts (e.g., 9am-12pm and 4pm-8pm)
                  </p>
                </div>
                <Switch
                  id="multiple-shifts"
                  checked={allowMultipleShifts}
                  onCheckedChange={setAllowMultipleShifts}
                  data-testid="multiple-shifts-toggle"
                />
              </div>

              {/* Shift Gap */}
              {allowMultipleShifts && (
                <div className="flex items-center gap-4 p-4 rounded-lg border">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="shift-gap" className="text-base font-medium">Minimum Gap Between Shifts</Label>
                    <p className="text-sm text-muted-foreground">
                      Required break time between an employee's shifts
                    </p>
                  </div>
                  <Select value={String(shiftGapMinutes)} onValueChange={(v) => setShiftGapMinutes(parseInt(v))}>
                    <SelectTrigger className="w-40" data-testid="shift-gap-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GAP_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Snap Interval */}
              <div className="flex items-center gap-4 p-4 rounded-lg border">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="snap-interval" className="text-base font-medium">Snap Interval</Label>
                  <p className="text-sm text-muted-foreground">
                    Shifts will snap to this time interval when dragging
                  </p>
                </div>
                <Select value={String(snapInterval)} onValueChange={(v) => setSnapInterval(parseInt(v))}>
                  <SelectTrigger className="w-40" data-testid="snap-interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SNAP_INTERVALS.map((interval) => (
                      <SelectItem key={interval.value} value={String(interval.value)}>
                        {interval.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Shift Patterns Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <Label htmlFor="shift-patterns" className="text-base font-medium">
                    Enable Shift Patterns
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Create reusable shift templates (e.g., "Morning Shift", "Closing Shift")
                  </p>
                </div>
                <Switch
                  id="shift-patterns"
                  checked={shiftPatternsEnabled}
                  onCheckedChange={setShiftPatternsEnabled}
                  data-testid="shift-patterns-toggle"
                />
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Admin User Dialog */}
      <Dialog open={adminUserDialogOpen} onOpenChange={setAdminUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAdminUser ? "Edit Admin User" : "Invite Admin User"}
            </DialogTitle>
            <DialogDescription>
              {editingAdminUser 
                ? "Update admin user details"
                : "Send an invitation email. They'll set their own password."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Name *</Label>
              <Input
                id="admin-name"
                value={adminUserForm.name}
                onChange={(e) => setAdminUserForm({ ...adminUserForm, name: e.target.value })}
                placeholder="e.g., Sarah Johnson"
                data-testid="admin-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email *</Label>
              <Input
                id="admin-email"
                type="email"
                value={adminUserForm.email}
                onChange={(e) => setAdminUserForm({ ...adminUserForm, email: e.target.value })}
                placeholder="e.g., sarah@company.com"
                disabled={editingAdminUser && editingAdminUser.status === 'invited'}
                data-testid="admin-user-email-input"
              />
              {!editingAdminUser && (
                <p className="text-xs text-muted-foreground">An invitation email will be sent to this address</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-role">Role</Label>
              <Select
                value={adminUserForm.user_role}
                onValueChange={(v) => setAdminUserForm({ ...adminUserForm, user_role: v })}
              >
                <SelectTrigger data-testid="admin-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminUserDialogOpen(false)} disabled={savingAdminUser}>
              Cancel
            </Button>
            <Button onClick={handleSaveAdminUser} disabled={savingAdminUser} data-testid="save-admin-user-btn">
              {savingAdminUser ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editingAdminUser ? "Saving..." : "Sending..."}</>
              ) : (
                <>
                  {editingAdminUser ? "Update" : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Admin User AlertDialog */}
      <AlertDialog open={!!deleteAdminUserDialog} onOpenChange={(open) => !open && setDeleteAdminUserDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Admin User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteAdminUserDialog?.name}"? 
              They will lose access to the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAdminUser}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Role Dialog */}
      <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRole ? 'Edit Custom Role' : 'Create Custom Role'}
            </DialogTitle>
            <DialogDescription>
              Define a custom role with specific permissions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Role Name *</Label>
              <Input
                id="role-name"
                placeholder="e.g., Shift Supervisor"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="role-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role-description">Description</Label>
              <Input
                id="role-description"
                placeholder="Brief description of this role"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                data-testid="role-description-input"
              />
            </div>

            {/* Permissions */}
            <div className="space-y-4">
              <Label>Permissions</Label>
              
              {/* Dashboard Access */}
              <div className="border rounded-lg p-4 bg-violet-50/50 dark:bg-violet-900/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Access Admin Dashboard</p>
                    <p className="text-xs text-muted-foreground">Can log into the manager portal</p>
                  </div>
                  <Checkbox
                    checked={roleFormData.permissions.can_access_admin_dashboard}
                    onCheckedChange={() => togglePermission('can_access_admin_dashboard')}
                  />
                </div>
              </div>

              {/* Rota/Calendar */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Rota / Calendar
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.rota_view} onCheckedChange={() => togglePermission('rota_view')} />
                    View
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.rota_create} onCheckedChange={() => togglePermission('rota_create')} />
                    Create
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.rota_edit} onCheckedChange={() => togglePermission('rota_edit')} />
                    Edit
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.rota_delete} onCheckedChange={() => togglePermission('rota_delete')} />
                    Delete
                  </label>
                </div>
              </div>

              {/* Employees */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Employees
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.employees_view} onCheckedChange={() => togglePermission('employees_view')} />
                    View
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.employees_create} onCheckedChange={() => togglePermission('employees_create')} />
                    Create
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.employees_edit} onCheckedChange={() => togglePermission('employees_edit')} />
                    Edit
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.employees_delete} onCheckedChange={() => togglePermission('employees_delete')} />
                    Delete
                  </label>
                </div>
              </div>

              {/* Shifts */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Shift Patterns
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.shifts_view} onCheckedChange={() => togglePermission('shifts_view')} />
                    View
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.shifts_create} onCheckedChange={() => togglePermission('shifts_create')} />
                    Create
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.shifts_edit} onCheckedChange={() => togglePermission('shifts_edit')} />
                    Edit
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.shifts_delete} onCheckedChange={() => togglePermission('shifts_delete')} />
                    Delete
                  </label>
                </div>
              </div>

              {/* Events */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <PartyPopper className="w-4 h-4" />
                  Events
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.events_view} onCheckedChange={() => togglePermission('events_view')} />
                    View
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.events_create} onCheckedChange={() => togglePermission('events_create')} />
                    Create
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.events_edit} onCheckedChange={() => togglePermission('events_edit')} />
                    Edit
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.events_delete} onCheckedChange={() => togglePermission('events_delete')} />
                    Delete
                  </label>
                </div>
              </div>

              {/* Absences */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <CalendarX className="w-4 h-4" />
                  Absences
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.absences_view} onCheckedChange={() => togglePermission('absences_view')} />
                    View
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.absences_create} onCheckedChange={() => togglePermission('absences_create')} />
                    Create
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.absences_edit} onCheckedChange={() => togglePermission('absences_edit')} />
                    Edit
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.absences_delete} onCheckedChange={() => togglePermission('absences_delete')} />
                    Delete
                  </label>
                </div>
              </div>

              {/* Reports */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Reports
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.reports_view} onCheckedChange={() => togglePermission('reports_view')} />
                    View Reports
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.reports_export} onCheckedChange={() => togglePermission('reports_export')} />
                    Export (CSV/PDF)
                  </label>
                </div>
              </div>

              {/* Wage Costs */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Wage Costs
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.wages_view} onCheckedChange={() => togglePermission('wages_view')} />
                    View Costs
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.wages_edit} onCheckedChange={() => togglePermission('wages_edit')} />
                    Edit Pay Rates
                  </label>
                </div>
              </div>

              {/* Settings & Admin */}
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-3 flex items-center gap-2">
                  <SettingsIcon className="w-4 h-4" />
                  Settings & Admin
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.settings_view} onCheckedChange={() => togglePermission('settings_view')} />
                    View Settings
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.settings_edit} onCheckedChange={() => togglePermission('settings_edit')} />
                    Edit Settings
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.billing_view} onCheckedChange={() => togglePermission('billing_view')} />
                    View Billing
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.billing_manage} onCheckedChange={() => togglePermission('billing_manage')} />
                    Manage Billing
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.roles_manage} onCheckedChange={() => togglePermission('roles_manage')} />
                    Manage Roles
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={roleFormData.permissions.users_manage} onCheckedChange={() => togglePermission('users_manage')} />
                    Assign User Roles
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRoleDialogOpen(false)} disabled={savingRole}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRole} 
              disabled={savingRole || !roleFormData.name.trim()}
              data-testid="save-role-btn"
            >
              {savingRole ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                editingRole ? 'Update Role' : 'Create Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role AlertDialog */}
      <AlertDialog open={!!deleteRoleDialog} onOpenChange={(open) => !open && setDeleteRoleDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the role "{deleteRoleDialog?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteRole}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
